import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { FolderOpen, Plus, Eye } from 'lucide-react'
import { formatDate, formatCurrency } from '@/lib/utils'
import type { Project } from '@/types/database-real'

interface ClientProjectsTabProps {
  projects: Project[]
  clientId: string
}

const statusVariants: Record<string, 'mint' | 'gold' | 'brown' | 'red' | 'default'> = {
  pending: 'default',
  in_progress: 'mint',
  completed: 'gold',
  cancelled: 'red',
  on_hold: 'brown',
}

const statusLabels: Record<string, string> = {
  pending: 'En attente',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé',
  on_hold: 'En pause',
}

export function ClientProjectsTab({ projects, clientId }: ClientProjectsTabProps) {
  if (projects.length === 0) {
    return (
      <EmptyState
        icon={<FolderOpen className="w-12 h-12" />}
        title="Aucun projet"
        description="Ce client n'a pas encore de projet."
        action={
          <Link to={`/projects/new?client=${clientId}`}>
            <Button leftIcon={<Plus className="w-4 h-4" />}>
              Créer un projet
            </Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Link to={`/projects/new?client=${clientId}`}>
          <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />}>
            Nouveau projet
          </Button>
        </Link>
      </div>

      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-beige-50 border-b border-beige-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Nom
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Statut
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Budget
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Créé le
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Échéance
                </th>
                <th className="text-right px-4 py-3 text-xs font-medium text-brown-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-beige-200">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-beige-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-brown-900">{project.title}</p>
                    {project.description && (
                      <p className="text-sm text-brown-500 truncate max-w-xs">
                        {project.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariants[project.status || 'pending']}>
                      {statusLabels[project.status || 'pending']}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-brown-600">
                    {project.budget ? formatCurrency(project.budget) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {project.created_at ? formatDate(project.created_at) : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-brown-500">
                    {project.deadline ? formatDate(project.deadline) : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/projects/${project.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
